/**
 * Hook pour empêcher l'écran de s'éteindre
 * @returns 
 */
import { useEffect, useRef } from "react";

interface WakeLockSentinel extends EventTarget {
  released: boolean;
  type: "screen";
  release(): Promise<void>;
}

interface WakeLockManager {
  request(type: "screen"): Promise<WakeLockSentinel>;
}

type NavigatorWithWakeLock = Navigator & {
  wakeLock?: WakeLockManager;
};

// Hook pour empêcher l'écran de s'éteindre
export const useWakeLock = (enabled: boolean = true) => {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    // Vérifier si l'API est supportée
    const nav = navigator as NavigatorWithWakeLock;
    if (!nav.wakeLock) {
      console.warn("[Wake Lock] API non supportée par ce navigateur");
      return;
    }

    if (!enabled) {
      return;
    }

    let isActive = true;

    const requestWakeLock = async () => {
      try {
        wakeLockRef.current = await nav.wakeLock!.request("screen");

        // Gérer la libération du wake lock
        wakeLockRef.current.addEventListener("release", () => { });

        // Réactiver automatiquement si l'écran se réveille
        const handleVisibilityChange = async () => {
          if (document.visibilityState === "visible" && isActive) {
            try {
              // Vérifier si le wake lock est toujours actif
              if (!wakeLockRef.current || wakeLockRef.current.released) {
                wakeLockRef.current = await nav.wakeLock!.request("screen");
              }
            } catch (error) {
              console.error(
                "[Wake Lock] Erreur lors de la réactivation:",
                error
              );
            }
          }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Nettoyer l'event listener
        return () => {
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
          );
        };
      } catch (error) {
        console.error("[Wake Lock] Erreur:", error);
        // L'erreur peut être due à des permissions ou à l'API non supportée
      }
    };

    requestWakeLock();

    // Nettoyer à la fermeture
    return () => {
      isActive = false;
      if (wakeLockRef.current && !wakeLockRef.current.released) {
        wakeLockRef.current.release().catch((error) => {
          console.error("[Wake Lock] Erreur lors de la libération:", error);
        });
      }
    };
  }, [enabled]);
};
